import { useMemo, type MouseEvent } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button, Table, Tooltip } from '@shared/components';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import type { FundAccountRow } from '../../../utils/fundAccountList';
import {
    NOT_INDEXED_LABEL,
    NOT_INDEXED_TOOLTIP,
    NOT_RECORDED_LABEL,
    UNMAPPED_LABEL,
} from '../../../utils/fundStatementLabels';
import { shortId } from '../../../utils/shortId';
import { UnsplitBadge } from './PeriodStatementBody/UnsplitBadge';

interface Props {
    rows: FundAccountRow[];
    selectedKey: string | null;
    currency: string;
    emptyMessage: string;
    onSelect: (row: FundAccountRow) => void;
}

interface FundAccountsTableMeta extends Record<string, unknown> {
    selectedKey: string | null;
    currency: string;
    onSelect: (row: FundAccountRow) => void;
}

const dealerLabel = (row: FundAccountRow): string => row.dealerCode ?? shortId(row.sellingDealerId);

function SageCell({ row, meta }: { row: FundAccountRow; meta: FundAccountsTableMeta }) {
    if (!row.selectable) {
        return (
            <Tooltip content={NOT_INDEXED_TOOLTIP}>
                <span tabIndex={0} className="text-mp-muted">{NOT_INDEXED_LABEL}</span>
            </Tooltip>
        );
    }
    const label = row.sageAccountCode ?? UNMAPPED_LABEL;
    return (
        <Button
            type="button"
            size="xs"
            variant="neutral"
            appearance="ghost"
            ariaPressed={meta.selectedKey === row.key}
            ariaLabel={`Show statement for ${label}, ${dealerLabel(row)}, ${row.productLabel}`}
            onClick={() => meta.onSelect(row)}
        >
            <span className={row.sageAccountCode ? undefined : 'text-mp-warning'}>{label}</span>
        </Button>
    );
}

function NetMovementCell({ row, currency }: { row: FundAccountRow; currency: string }) {
    const figures = row.vatFigures;
    if (figures === null) {
        return (
            <span className="block text-right tabular-nums">
                <span aria-hidden="true">{'–'}</span>
                <span className="sr-only">{NOT_RECORDED_LABEL}</span>
            </span>
        );
    }
    return (
        <span className="flex items-center justify-end gap-2">
            {figures.unsplit.postings > 0 && <UnsplitBadge postings={figures.unsplit.postings} />}
            <span className="text-right tabular-nums">{formatVerbatimMoneyCurrency(figures.net, currency)}</span>
        </span>
    );
}

// Module scope keeps cell identities stable; live values arrive through `table.options.meta`.
const columns: ColumnDef<FundAccountRow>[] = [
    {
        id: 'sage',
        header: 'Sage account',
        cell: ({ row, table }) => <SageCell row={row.original} meta={table.options.meta as FundAccountsTableMeta} />,
    },
    { id: 'dealer', header: 'Dealer', cell: ({ row }) => <span title={row.original.sellingDealerId}>{dealerLabel(row.original)}</span> },
    { id: 'product', header: 'Product', cell: ({ row }) => row.original.productLabel },
    {
        id: 'movement',
        header: 'Movement (gross)',
        cell: ({ row, table }) => {
            const { currency } = table.options.meta as FundAccountsTableMeta;
            return (
                <span className="block text-right tabular-nums">
                    {formatVerbatimMoneyCurrency(row.original.amount, currency)}
                </span>
            );
        },
    },
    {
        id: 'movementNet',
        header: 'Movement (net)',
        cell: ({ row, table }) => (
            <NetMovementCell row={row.original} currency={(table.options.meta as FundAccountsTableMeta).currency} />
        ),
    },
];

const getRowId = (row: FundAccountRow) => row.key;

function clickedBodyRowIndex(event: MouseEvent<HTMLDivElement>): number | null {
    const { target, currentTarget } = event;
    if (!(target instanceof Element) || !currentTarget.contains(target)) return null;
    if (target.closest('button, a, input, select, textarea, label, [role="button"]')) return null;
    const row = target.closest('tbody > tr');
    return row instanceof HTMLTableRowElement ? row.sectionRowIndex : null;
}

export function FundAccountsDataTable({ rows, selectedKey, currency, emptyMessage, onSelect }: Props) {
    const meta = useMemo<FundAccountsTableMeta>(
        () => ({ selectedKey, currency, onSelect }),
        [selectedKey, currency, onSelect],
    );
    // Mouse-only row selection: the shared Table's onRowClick would make every row a tab stop.
    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        const index = clickedBodyRowIndex(event);
        const row = index === null ? undefined : rows[index];
        if (row?.selectable) onSelect(row);
    };
    return (
        <div onClick={onClick}>
            <Table<FundAccountRow>
                data={rows}
                columns={columns}
                meta={meta}
                enableSorting={false}
                emptyMessage={emptyMessage}
                getRowId={getRowId}
                selectedRowId={selectedKey}
            />
        </div>
    );
}
